/**
 * VideoConfigTabContent — Config tab content for the Video Generator bottom panel.
 *
 * Contains: reference image picker slot, motion prompt slot, control bar
 * (config pill, retry selector, sparks count, generate button), and the config flyout.
 *
 * The flyout uses absolute positioning (no createPortal) — rendered inside a
 * relative wrapper around the config pill button.
 */

import { memo, useState, useRef, useEffect, useCallback } from 'react'
import { VideoConfigFlyout } from './VideoConfigFlyout'

/** Video generation sparks cost: (model, resolution) -> base sparks per generation */
const VIDEO_SPARKS_TABLE: Record<string, Record<string, number>> = {
  'seedance-2.0-image-to-video': { '480p': 15, '720p': 20 },
  'seedance-2.0-reference-to-video': { '480p': 15, '720p': 20 },
  'kling-v3-pro': { auto: 5 },
  'veo-3.1': { '720p': 8, '1080p': 12, '4k': 20 },
}

/** Resolve display name for video model */
const MODEL_DISPLAY_NAME: Record<string, string> = {
  'seedance-2.0-image-to-video': 'Seedance I2V',
  'seedance-2.0-reference-to-video': 'Seedance R2V',
  'kling-v3-pro': 'Kling',
  'veo-3.1': 'Veo',
}

function getVideoSparks(model: string, resolution: string, retryCount: number): number {
  const modelTable = VIDEO_SPARKS_TABLE[model] ?? VIDEO_SPARKS_TABLE['kling-v3-pro']
  const baseCost = modelTable[resolution] ?? Object.values(modelTable)[0] ?? 5
  return baseCost * retryCount
}

export interface VideoConfigTabContentProps {
  model: string
  onModelChange: (model: string) => void
  referenceImagePicker?: React.ReactNode
  motionPromptContent?: React.ReactNode
  duration: number
  resolution: string
  aspectRatio: string
  generateAudio: boolean
  onDurationChange: (duration: number) => void
  onResolutionChange: (resolution: string) => void
  onAspectRatioChange: (aspectRatio: string) => void
  onGenerateAudioChange: (generateAudio: boolean) => void
  isRunning: boolean
  onGenerate: (retryCount: number) => void
  activeTab: string
}

export const VideoConfigTabContent = memo(function VideoConfigTabContent({
  model,
  onModelChange,
  referenceImagePicker,
  motionPromptContent,
  duration,
  resolution,
  aspectRatio,
  generateAudio,
  onDurationChange,
  onResolutionChange,
  onAspectRatioChange,
  onGenerateAudioChange,
  isRunning,
  onGenerate,
  activeTab,
}: VideoConfigTabContentProps) {
  const [retryCount, setRetryCount] = useState(1)
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false)
  const [isRetryPopoverOpen, setIsRetryPopoverOpen] = useState(false)

  const flyoutRef = useRef<HTMLDivElement>(null)
  const pillRef = useRef<HTMLButtonElement>(null)
  const retryPillRef = useRef<HTMLButtonElement>(null)
  const retryPopoverRef = useRef<HTMLDivElement>(null)

  // Computed values
  const sparksCount = getVideoSparks(model, resolution, retryCount)
  const audioIcon = generateAudio ? '\u{1F50A}' : '\u{1F507}'
  const modelDisplayName = MODEL_DISPLAY_NAME[model] ?? model
  const configPillLabel = `${modelDisplayName} \u00B7 ${aspectRatio} \u00B7 ${resolution === 'auto' ? 'Auto' : resolution === '4k' ? '4K' : resolution} \u00B7 ${duration}s \u00B7 ${audioIcon}`

  // Click-outside for flyout
  useEffect(() => {
    if (!isFlyoutOpen) return
    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement
      if (
        flyoutRef.current && !flyoutRef.current.contains(target) &&
        pillRef.current && !pillRef.current.contains(target)
      ) {
        setIsFlyoutOpen(false)
      }
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setIsFlyoutOpen(false)
      }
    }
    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isFlyoutOpen])

  // Close flyout when switching away from config tab
  useEffect(() => {
    if (activeTab !== 'config') {
      setIsFlyoutOpen(false)
    }
  }, [activeTab])

  // Click-outside for retry popover
  useEffect(() => {
    if (!isRetryPopoverOpen) return
    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement
      if (
        retryPopoverRef.current && !retryPopoverRef.current.contains(target) &&
        retryPillRef.current && !retryPillRef.current.contains(target)
      ) {
        setIsRetryPopoverOpen(false)
      }
    }
    document.addEventListener('pointerdown', handlePointerDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
    }
  }, [isRetryPopoverOpen])

  // Generate handler
  const handleGenerate = useCallback(() => {
    onGenerate(retryCount)
  }, [onGenerate, retryCount])

  return (
    <div className="h-full flex flex-col nowheel">
      {/* Scrollable config content */}
      <div className="flex-1 p-3 space-y-3 overflow-y-auto">
        {referenceImagePicker}
        {motionPromptContent}
      </div>

      {/* Control Bar -- fixed at bottom */}
      <div className="flex items-center gap-2 px-3 py-1 border-t border-border-subtle" data-testid="video-control-bar">
        {/* Config Pill */}
        <div className="relative">
          <button
            ref={pillRef}
            onClick={() => setIsFlyoutOpen(!isFlyoutOpen)}
            className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-surface-2 text-text-muted hover:text-foreground border border-border-subtle hover:border-primary/40 transition-colors whitespace-nowrap"
            data-testid="video-config-pill"
          >
            {configPillLabel}
          </button>

          {/* Config Flyout -- absolute, no portal */}
          {isFlyoutOpen && (
            <div
              ref={flyoutRef}
              className="absolute bottom-full left-0 mb-2 z-50 bg-surface-1 border border-border rounded-xl shadow-2xl p-3 min-w-[260px]"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              data-testid="video-config-flyout"
            >
              <VideoConfigFlyout
                model={model}
                onModelChange={onModelChange}
                aspectRatio={aspectRatio}
                onAspectRatioChange={onAspectRatioChange}
                resolution={resolution}
                onResolutionChange={onResolutionChange}
                duration={duration}
                onDurationChange={onDurationChange}
                generateAudio={generateAudio}
                onGenerateAudioChange={onGenerateAudioChange}
              />
            </div>
          )}
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Retry Count Pill + Popover */}
        <div className="relative" data-testid="video-retry-selector">
          <button
            ref={retryPillRef}
            onClick={() => setIsRetryPopoverOpen(!isRetryPopoverOpen)}
            className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-surface-2 text-text-muted hover:text-foreground border border-border-subtle hover:border-primary/40 transition-colors whitespace-nowrap"
            data-testid="video-retry-pill"
          >
            {retryCount}x
          </button>

          {isRetryPopoverOpen && (
            <div
              ref={retryPopoverRef}
              className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-surface-1 border border-border rounded-lg shadow-2xl overflow-hidden z-50 min-w-[48px]"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              data-testid="video-retry-popover"
            >
              {([4, 3, 2, 1] as const).map((n) => (
                <button
                  key={n}
                  onClick={() => {
                    setRetryCount(n)
                    setIsRetryPopoverOpen(false)
                  }}
                  className={`w-full px-3 py-1.5 text-xs font-medium text-center transition-colors ${
                    retryCount === n
                      ? 'text-primary-foreground'
                      : 'text-text-muted hover:text-foreground hover:bg-surface-2'
                  }`}
                  style={
                    retryCount === n
                      ? { backgroundColor: 'var(--primary)' }
                      : undefined
                  }
                  data-testid={`video-retry-${n}x`}
                >
                  {n}x
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sparks Count */}
        <span className="flex items-center gap-1 text-xs text-text-muted tabular-nums" data-testid="video-sparks-count">
          <svg className="w-3.5 h-3.5 text-yellow-400 shrink-0" viewBox="0 0 24 24" fill="currentColor">
            <path d="M13 3L4 14h7l-2 7 9-11h-7l2-7z" />
          </svg>
          {sparksCount}
        </span>

        {/* Generate Button */}
        <button
          onClick={handleGenerate}
          disabled={isRunning}
          className="w-6 h-6 flex items-center justify-center rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ backgroundColor: 'var(--primary)' }}
          title="Generate"
          data-testid="video-generate-button"
        >
          <svg className="w-3.5 h-3.5 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      </div>
    </div>
  )
})

VideoConfigTabContent.displayName = 'VideoConfigTabContent'
